import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from 'src/components/new/Typography';
import { clearUnsavedWeightChanges, updateUnsavedWeightChanges } from 'src/features/admin/adminSlice';
import { getWeightCriticalInput, updateWeightInputs } from 'src/features/admin/utils';
import { AppDispatch, RootState } from 'src/store/store';
import { Button } from 'src/components/new/Button';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PendingIcon from '@mui/icons-material/Pending';
import DeleteIcon from '@mui/icons-material/Delete';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import BkLoader from 'src/components/loader/BkLoader';
import { validateInput } from './utils/utils';

const mapWeighTitle = {
  sheet: 'Per Sheet of Paper',
  '#10': 'Per #10 Envelope',
  '6x9': 'Per 6x9 Envelope',
  flat: 'Per Flat Envelope',
  flat_rate_envelope: 'Per Flat Rate Envelope',
  legal_flat_rate_envelope: 'Per Legal Flat Rate Envelope',
  flat_rate_box: 'Per Flat Rate Box',
  return_envelope: 'Per Return Envelope',
};

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const WeightCriticalInput: React.FC = () => {
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });
  const dispatch = useDispatch<AppDispatch>();
  const weightInputs = useSelector((state: RootState) => state.admin.weightCriticalInput);
  const unsavedWeightInputChanges = useSelector((state: RootState) => state.admin.unsavedWeightChanges);
  const unsavedChangesNames = Object.keys(unsavedWeightInputChanges);
  const [isEditingEnabled, setIsEditingEnabled] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const onEditClick = () => {
    setIsEditingEnabled(true);
  };

  const onDiscardChangesClick = () => {
    setIsEditingEnabled(false);
    dispatch(clearUnsavedWeightChanges());
    setShowSnackbarMsg({ msg: 'Changes discarded!', type: 'success' });
  };

  useEffect(() => {
    dispatch(getWeightCriticalInput());
  }, []);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    const isValidInput = validateInput(e.target.value, 3);
    if (!isValidInput) return;
    let value = e.target.value.replace(/[^0-9.]/g, '');
    let parts = value.split('.');
    if (parts.length > 2) {
      parts = [parts[0], parts.slice(1).join('')];
    }
    if (parts[1]) {
      parts[1] = parts[1].slice(0, 3);
    }
    value = parts.join('.');
    dispatch(updateUnsavedWeightChanges({ name, value }));
  };

  const onSaveChangesClick = () => {
    if (unsavedChangesNames.length === 0) return;
    if (Object.values(unsavedWeightInputChanges).some((item) => item.length === 1)) {
      setShowSnackbarMsg({ msg: 'Please complete all fields before saving', type: 'error' });
      return;
    }
    setShowLoader(true);
    dispatch(updateWeightInputs(unsavedWeightInputChanges)).then((response) => {
      if (updateWeightInputs.fulfilled.match(response)) {
        setShowSnackbarMsg({ msg: 'Changes saved successfully!', type: 'success' });
        dispatch(getWeightCriticalInput()).then(() => {
          dispatch(clearUnsavedWeightChanges());
          setShowLoader(false);
        });
      }
      if (updateWeightInputs.rejected.match(response)) {
        setShowSnackbarMsg({ msg: 'Something went wrong!', type: 'error' });
        setShowLoader(false);
      }
    });
    setIsEditingEnabled(false);
  };

  useEffect(() => {
    return () => {
      dispatch(clearUnsavedWeightChanges());
    };
  }, []);

  return (
    <>
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
      <BkLoader open={showLoader} loaderTitle="Updating weights..." />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 3 }}>
          <Typography sx={{ fontFamily: 'ABC Arizona Mix' }} fontWeight={300} fontSize={32}>
            Weight
          </Typography>
          {unsavedChangesNames.length !== 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: '#ffeedf',
                borderRadius: '999px',
                padding: 1,
              }}
            >
              <PendingIcon sx={{ display: 'inline', color: '#7b3f07' }} />
              <Typography sx={{ color: '#7b3f07', fontWeight: 500 }}>
                {unsavedChangesNames.length} unsaved changes
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flex: 1.5 }}>
          {isEditingEnabled && (
            <>
              <Button startIcon={<DeleteIcon fontSize="small" />} type="text" onClick={onDiscardChangesClick}>
                Discard Changes
              </Button>
              <Button startIcon={<CheckIcon fontSize="small" />} type="primary" onClick={onSaveChangesClick}>
                Save Changes
              </Button>
            </>
          )}
          {!isEditingEnabled && (
            <Button startIcon={<EditIcon fontSize="small" />} type="primary" onClick={onEditClick}>
              Edit Weight
            </Button>
          )}
          <Button startIcon={<FileDownloadOutlinedIcon />} type="outline">
            Export
          </Button>
        </Box>
      </Box>
      <Box sx={{ width: '30%' }}>
        <Box sx={{ backgroundColor: '#cee2ff', padding: 1 }}>
          <Typography fontWeight={550}>Weight: Critical Inputs</Typography>
        </Box>
        {weightInputs.map((item) => {
          return (
            <Box
              sx={{
                padding: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #7e828b',
              }}
              key={item.id}
            >
              <Typography fontSize={14} fontWeight={500}>
                {mapWeighTitle[item.name as keyof typeof mapWeighTitle]}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <input
                  style={{
                    height: '3rem',
                    borderRadius: '0.3125rem',
                    textAlign: 'right',
                    border: '0.0625rem solid #7e828b',
                    backgroundColor: unsavedChangesNames.includes(item.name) ? '#ffeedf' : '#f6f6f6',
                    borderColor: unsavedChangesNames.includes(item.name) ? '#7b3f07' : '',
                    color: isEditingEnabled ? '' : '#7e828b',
                    marginRight: '1rem',
                  }}
                  onChange={(e) => onInputChange(e, item.name)}
                  value={unsavedWeightInputChanges[item.name] ?? item.weight.toFixed(3)}
                  id={item.id.toString()}
                  disabled={!isEditingEnabled}
                />
                <Typography>oz</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default WeightCriticalInput;
